import type { Message, SessionStorageType } from '../types';
import { formatSessionDate as formatLocalizedSessionDate } from './i18n';
import {
  CURRENT_SESSION_KEY,
  SESSION_DATA_PREFIX,
  SESSIONS_METADATA_KEY,
  readScopedItem,
  scopedStorageKey,
  type StorageScope,
} from '../constants/storage';

export interface SessionMetadata {
  id: string;
  title: string;
  createdAt: string;
  lastModified: string;
  messageCount: number;
  preview: string;
}

export interface SessionData {
  id: string;
  title: string;
  createdAt: string;
  lastModified: string;
  messages: Message[];
  threadId?: string | null;
}

const PREVIEW_LIMIT = 160;

function getStorage(kind: SessionStorageType = 'local'): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return kind === 'session' ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function getSessionKey(id: string): string {
  return `${SESSION_DATA_PREFIX}${id}`;
}

// The session list and current-session pointer are scoped per org/agent (see
// scopedStorageKey); per-session data keys stay global since ids are unique.
export function loadSessionMetadata(
  storageKind: SessionStorageType = 'local',
  scope?: StorageScope
): SessionMetadata[] {
  const storage = getStorage(storageKind);
  if (!storage) return [];
  let raw: string | null = null;
  try {
    raw = readScopedItem(storage, SESSIONS_METADATA_KEY, scope);
  } catch {
    // ignore
  }
  const parsed = safeParse<SessionMetadata[]>(raw, []);
  return Array.isArray(parsed) ? parsed : [];
}

export function saveSessionMetadata(
  list: SessionMetadata[],
  storageKind: SessionStorageType = 'local',
  scope?: StorageScope
): void {
  const storage = getStorage(storageKind);
  if (!storage) return;
  try {
    storage.setItem(scopedStorageKey(SESSIONS_METADATA_KEY, scope), JSON.stringify(list));
  } catch {
    // ignore
  }
}

export function loadSession(
  id: string,
  storageKind: SessionStorageType = 'local'
): SessionData | null {
  const storage = getStorage(storageKind);
  if (!storage) return null;
  return safeParse<SessionData | null>(storage.getItem(getSessionKey(id)), null);
}

export function saveSession(data: SessionData, storageKind: SessionStorageType = 'local'): void {
  const storage = getStorage(storageKind);
  if (!storage) return;
  try {
    storage.setItem(getSessionKey(data.id), JSON.stringify(data));
  } catch {
    // ignore
  }
}

export function deleteSession(id: string, storageKind: SessionStorageType = 'local'): void {
  const storage = getStorage(storageKind);
  if (!storage) return;
  try {
    storage.removeItem(getSessionKey(id));
  } catch {
    // ignore
  }
}

export function getCurrentSessionId(
  storageKind: SessionStorageType = 'local',
  scope?: StorageScope
): string | null {
  const storage = getStorage(storageKind);
  if (!storage) return null;
  try {
    return readScopedItem(storage, CURRENT_SESSION_KEY, scope);
  } catch {
    return null;
  }
}

export function setCurrentSessionId(
  id: string | null,
  storageKind: SessionStorageType = 'local',
  scope?: StorageScope
): void {
  const storage = getStorage(storageKind);
  if (!storage) return;
  try {
    const key = scopedStorageKey(CURRENT_SESSION_KEY, scope);
    if (!id) storage.removeItem(key);
    else storage.setItem(key, id);
  } catch {
    // ignore
  }
}

export function createSessionId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return `sess_${crypto.randomUUID()}`;
    }
  } catch {
    // ignore
  }
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function deriveSessionTitle(messages: Message[]): string {
  const firstUser = messages.find(msg => msg.sender === 'user' && msg.text && msg.text.trim());
  if (firstUser?.text) return firstUser.text.trim().slice(0, 60);
  return 'New conversation';
}

export function deriveSessionPreview(messages: Message[]): string {
  const combined = messages
    .map(msg => (msg.text || '').trim())
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  return combined.length > PREVIEW_LIMIT ? `${combined.slice(0, PREVIEW_LIMIT)}…` : combined;
}

/** Locale-aware session date; kept here for backward compatibility with older imports. */
export function formatSessionDate(isoString: string, locale = 'en'): string {
  return formatLocalizedSessionDate(isoString, locale);
}
